import { SyncFieldCategory } from './woocommerce';
import { FieldMapping } from './fieldMapping';

// Sync strategija - kako se pokreće sinhronizacija
export type SyncStrategy =
  | 'manual'      // Samo ručno pokretanje
  | 'scheduled'   // Scheduled sync sa delta (modified_after)
  | 'webhook';    // Real-time webhook sync

// Sync metoda - KAKO se sync izvršava
export type SyncMethod =
  | 'batch_all'   // Dohvati sve, usporedi, pa šalji (brže za mali broj promjena)
  | 'streaming';  // Dohvati batch, odmah ažuriraj, pa sljedeći batch (manje RAM-a, real-time progress)

export interface Shop {
  id: string;
  name: string;
  url: string;
  consumerKey: string;
  consumerSecret: string;
  isActive: boolean;
  createdAt: string;
  lastSyncAt?: string;
  /** Timestamp zadnjeg USPJEŠNOG synca - koristi se za delta sync (modified_after) */
  lastSuccessfulSyncAt?: string;
  productCount?: number;
  syncProfile?: ShopSyncProfile;
  /** Field mappings za ovaj shop */
  fieldMappings?: FieldMapping[];
  /** Scheduler konfiguracija za automatski sync */
  scheduleConfig?: ScheduleConfig;
  /** Taxonomy mappings (kategorije i tagovi) */
  taxonomyMappings?: ShopTaxonomyMappings;
  /** Sync strategija */
  syncStrategy?: SyncStrategy;
  /** Sync metoda */
  syncMethod?: SyncMethod;
  /** Webhook konfiguracija (za webhook strategiju) */
  webhookConfig?: WebhookConfig;
}

// Scheduler konfiguracija
export interface ScheduleConfig {
  // Da li je scheduler uključen
  enabled: boolean;
  // Tip schedulera
  type: 'browser' | 'vercel_cron';
  // Interval u minutama (za browser scheduler)
  intervalMinutes: number;
  // Cron expression (za Vercel Cron) npr. "0 */2 * * *" za svaka 2 sata
  cronExpression?: string;
  // Datum i vrijeme zadnjeg izvršavanja
  lastRun?: string;
  // Datum i vrijeme sljedećeg izvršavanja
  nextRun?: string;
  // Status zadnjeg izvršavanja
  lastRunStatus?: 'success' | 'error' | 'running';
  // Poruka o grešci (ako je bilo grešaka)
  lastRunError?: string;
  // Cron sync opcije
  cronOptions?: CronSyncOptions;
}

// Opcije za cron sinhronizaciju
export interface CronSyncOptions {
  // Batch size - koliko proizvoda po pozivu (default: 100)
  batchSize: number;
  // Reset interval u minutama - nakon koliko vremena kreće novi ciklus (default: 60)
  resetIntervalMinutes: number;
  // Koristi modified_after filter - samo proizvodi promijenjeni od zadnjeg synca
  useModifiedAfter: boolean;
  // Modified after interval u minutama - koliko unazad gledati (default: 65, malo više od reset intervala)
  modifiedAfterMinutes: number;
  // Da li nastaviti gdje je stao (koristi Redis state)
  resumeFromLastPosition: boolean;
}

// Detaljan sync profil za svaki shop
export interface ShopSyncProfile {
  // Režim sinhronizacije
  mode: SyncMode;

  // Koje kategorije polja sinhronizirati
  fieldsToSync: SyncFieldCategory[];

  // Specifične opcije
  options: {
    // Kreiranje novih artikala
    createNewProducts: boolean;
    // Status za nove proizvode (publish, draft, pending, private)
    // Ako nije postavljeno, koristi status iz source shopa
    newProductStatus?: 'publish' | 'draft' | 'pending' | 'private' | 'source';
    // Ažuriranje postojećih artikala
    updateExistingProducts: boolean;
    // Brisanje artikala koji ne postoje u source-u
    deleteOrphanedProducts: boolean;
    // Konsolidacija duplikata SKU
    consolidateDuplicates: boolean;
    // Preskoči artikle bez SKU
    skipProductsWithoutSku: boolean;
    // Preskoči artikle sa _updateprice = 'NE'
    respectUpdatePriceFlag: boolean;
    // Pricing ponašanje
    pricingBehavior: {
      // Za NOVE proizvode: calculate=kalkuliraj iz nabavne, source=kopiraj sa source-a, skip=ne postavljaj
      newProducts: 'calculate' | 'source' | 'skip';
      // Za POSTOJEĆE proizvode: calculate=kalkuliraj, source=kopiraj, skip=ne diraj
      existingProducts: 'calculate' | 'source' | 'skip';
    };
    // Meta polje na TARGET shopu koje sadrži ID proizvoda iz SOURCE shopa
    // Koristi se za webhook delete i pronalaženje povezanih proizvoda
    // Npr. '_source_product_id', '_import_id', '_wpai_source_id'
    sourceProductIdField?: string;
  };

  // Pricing opcije
  pricing: PricingConfig;

  // Filteri - koje artikle sinhronizirati
  filters: {
    // Po statusu (publish, draft, etc.)
    status?: string[];
    // Po kategoriji (ID-evi)
    categoryIds?: number[];
    // Po brendu
    brands?: string[];
    // Po dobavljaču
    suppliers?: string[];
    // Po stock statusu
    stockStatus?: ('instock' | 'outofstock' | 'onbackorder')[];
    // SKU pattern (regex)
    skuPattern?: string;
    // Minimum stock
    minStock?: number;
    // Custom meta filteri
    customMeta?: CustomMetaFilter[];
  };

  // Custom meta polja za sinhronizaciju
  customMetaKeys?: string[];

  // Mapiranje custom meta polja (source -> target)
  // Npr. source "_cost" -> target "_nabavna"
  customFieldMappings?: CustomFieldMapping[];

  // Conflict resolution - šta raditi kad target već ima vrijednost
  conflictResolution?: FieldConflictResolution;

  // Duplicate resolution - kako riješiti duplikate SKU
  duplicateResolution?: DuplicateResolutionConfig;

  // Izvor slika za sinhronizaciju
  imageSource?: ImageSourceConfig;

  // AI generiranje opisa
  aiDescription?: AIDescriptionConfig;

  // Mapiranje atributa (source -> target)
  // Npr. "pa_boja" -> "pa_color"
  attributeMappings?: AttributeMappingConfig;

  // Pravila naziva po dobavljaču
  // Omogućava transformaciju naziva ovisno o dobavljaču
  nameRules?: NameRulesConfig;

  // Menu order po dobavljaču
  // Omogućava postavljanje menu_order vrijednosti za WooCommerce proizvode po dobavljaču
  supplierMenuOrder?: SupplierMenuOrderConfig;
}

// ============================================
// SUPPLIER MENU ORDER KONFIGURACIJA
// ============================================

// Konfiguracija menu_order vrijednosti po dobavljaču
export interface SupplierMenuOrderConfig {
  // Da li je menu order po dobavljaču uključen
  enabled: boolean;
  // Mapiranje dobavljača na menu_order vrijednost
  // Npr. { "ASBIS": 1, "DRINEX": 2, "UNIEXPERT": 3 }
  orders: Record<string, number>;
  // Default menu_order za dobavljače koji nisu na listi
  defaultOrder: number;
}

// Default supplier menu order config
export const DEFAULT_SUPPLIER_MENU_ORDER_CONFIG: SupplierMenuOrderConfig = {
  enabled: false,
  orders: {},
  defaultOrder: 0,
};

// Tipovi vrijednosti za custom field mapping
export type CustomFieldValueType = 'text' | 'price' | 'number';

// Mapiranje custom meta polja između source i target shopa
export interface CustomFieldMapping {
  // Naziv meta polja u source shopu
  sourceField: string;
  // Naziv meta polja u target shopu
  targetField: string;
  // Da li je mapiranje aktivno
  enabled: boolean;
  // Opis (opcionalno, za UI)
  description?: string;
  // Tip vrijednosti - ako je 'price', formatira kao cijenu (255,00 → 255)
  valueType?: CustomFieldValueType;
}

// ============================================
// ATTRIBUTE MAPPING (Mapiranje atributa)
// ============================================

// Mapiranje atributa između source i target shopa
// Npr. "pa_boja" → "pa_color", "Boja" → "Color"
export interface AttributeMapping {
  // Naziv/slug atributa u source shopu (npr. "pa_boja" ili "Boja")
  sourceName: string;
  // Naziv/slug atributa u target shopu (npr. "pa_color" ili "Color")
  targetName: string;
  // Da li je mapiranje aktivno
  enabled: boolean;
}

// Konfiguracija mapiranja atributa
export interface AttributeMappingConfig {
  // Da li je mapiranje atributa uključeno
  enabled: boolean;
  // Lista mapiranja
  mappings: AttributeMapping[];
  // Šta raditi sa atributima koji nisu mapirani
  // 'keep_original' = zadrži originalni naziv
  // 'skip' = preskoči atribut
  unmappedBehavior: 'keep_original' | 'skip';
}

// ============================================
// NAME RULES (Pravila naziva po dobavljaču)
// ============================================

// Izvor naziva proizvoda
export type NameSourceType = 'name' | 'description' | 'short_description' | 'sku' | 'custom_field';

// Transformacije koje se mogu primijeniti na naziv
export interface NameTransformation {
  // Uzmi samo prvih X riječi
  maxWords?: number;
  // Ukloni određene znakove (npr. "(", ")", ",")
  removeChars?: string[];
  // Zamijeni znakove (npr. "_" -> " ")
  replaceChars?: { from: string; to: string }[];
  // Dodaj prefix (npr. "[ASBIS] ")
  prefix?: string;
  // Dodaj suffix (npr. " - Novo")
  suffix?: string;
  // Pretvori u UPPERCASE, lowercase, ili Title Case
  textCase?: 'uppercase' | 'lowercase' | 'titlecase' | 'none';
  // Ukloni HTML tagove
  stripHtml?: boolean;
  // Trim whitespace
  trim?: boolean;
}

// Pravilo naziva za određenog dobavljača
export interface NameRule {
  // Naziv dobavljača (vrijednost iz kolone 'dobavljac')
  supplierName: string;
  // Da li je pravilo aktivno
  enabled: boolean;
  // Izvor naziva
  source: NameSourceType;
  // Custom field key (ako je source = 'custom_field')
  customFieldKey?: string;
  // Transformacije
  transformation: NameTransformation;
  // Prioritet (veći broj = veći prioritet)
  priority: number;
}

// Konfiguracija pravila naziva
export interface NameRulesConfig {
  // Da li su pravila naziva uključena
  enabled: boolean;
  // Lista pravila po dobavljačima
  rules: NameRule[];
  // Default pravilo za dobavljače koji nisu na listi
  defaultRule?: {
    source: NameSourceType;
    customFieldKey?: string;
    transformation: NameTransformation;
  };
}

// Default name transformation
export const DEFAULT_NAME_TRANSFORMATION: NameTransformation = {
  stripHtml: true,
  trim: true,
  textCase: 'none',
};

// Default name rules config
export const DEFAULT_NAME_RULES_CONFIG: NameRulesConfig = {
  enabled: false,
  rules: [],
};

// Formatira vrijednost cijene (255,00 → 255, 1.234,56 → 1234.56)
export function formatPriceValue(value: any): string {
  if (value === null || value === undefined || value === '') return '';

  let strValue = String(value).trim();

  // Ako ima i točku i zarez, pretpostavi europski format (1.234,56)
  if (strValue.includes(',') && strValue.includes('.')) {
    // Europski format: 1.234,56 → 1234.56
    strValue = strValue.replace(/\./g, '').replace(',', '.');
  } else if (strValue.includes(',')) {
    // Samo zarez: 255,00 → 255.00
    strValue = strValue.replace(',', '.');
  }

  // Parsiraj kao broj
  const num = parseFloat(strValue);
  if (isNaN(num)) return strValue; // Vrati original ako nije broj

  // Ako je cijeli broj, vrati bez decimala
  if (Number.isInteger(num)) {
    return String(Math.round(num));
  }

  // Inače vrati s 2 decimale
  return num.toFixed(2);
}

// Režim sinhronizacije
export type SyncMode =
  | 'full'      // Kreiranje + Ažuriranje + Brisanje
  | 'create'    // Samo kreiranje novih
  | 'update'    // Samo ažuriranje postojećih
  | 'smart';    // Kreiranje + Ažuriranje (bez brisanja)

// Conflict Resolution strategije za polja
export type FieldConflictStrategy =
  | 'overwrite'       // Uvijek prepiši sa source vrijednošću
  | 'skip_if_exists'  // Preskoči ako target već ima vrijednost
  | 'merge_append'    // Dodaj source na kraj (za liste/arrays)
  | 'merge_prepend'   // Dodaj source na početak (za liste/arrays)
  | 'keep_higher'     // Zadrži veću vrijednost (za brojeve)
  | 'keep_lower'      // Zadrži manju vrijednost (za brojeve)
  | 'keep_newer'      // Zadrži noviju vrijednost (za datume)
  | 'use_mapping'     // Koristi mapiranje iz Taxonomy Mapping panela (za kategorije/tagove)
  | 'custom';         // Custom logika (definisana u mappingu)

// Conflict resolution po kategoriji polja
export interface FieldConflictResolution {
  // Default strategija za sve
  default: FieldConflictStrategy;

  // Override po kategoriji
  byCategory?: Partial<Record<SyncFieldCategory, FieldConflictStrategy>>;

  // Override po specifičnom polju
  byField?: Record<string, FieldConflictStrategy>;
}

// Duplicate Resolution - kako riješiti duplikate SKU
export interface DuplicateResolutionConfig {
  // Da li je uključeno
  enabled: boolean;

  // Koje meta polje označava dobavljača u source shopu
  supplierField: string;

  // Lista dobavljača sa prioritetima (manji broj = veći prioritet)
  // Dobavljači koji nisu na listi imaju najniži prioritet
  supplierPriorities: SupplierPriority[];

  // Šta raditi kad nema dobavljača ili nije na listi prioriteta
  fallbackStrategy: 'first' | 'last' | 'highest_stock' | 'lowest_price' | 'skip';

  // Šta uraditi sa duplikatima: draft ili delete
  duplicateAction: 'draft' | 'delete';
}

export interface SupplierPriority {
  // Naziv dobavljača (kako se pojavljuje u meta polju)
  name: string;
  // Prioritet (1 = najviši)
  priority: number;
  // Da li je aktivan
  enabled: boolean;
}

// ============================================
// PRICING KONFIGURACIJA
// ============================================

// Pravilo cijene po cjenovnom rasponu nabavne cijene
export interface PriceRangeRule {
  // Minimalna nabavna cijena (uključivo)
  minPrice: number;
  // Maksimalna nabavna cijena (isključivo, null = bez limita)
  maxPrice: number | null;
  // Izračun REDOVNE cijene za ovaj raspon
  regularPriceCalculation: PriceCalculation;
  // Izračun AKCIJSKE cijene za ovaj raspon (opcionalno)
  salePriceCalculation?: SalePriceCalculation;
  // Da li je pravilo aktivno
  enabled: boolean;
}

// Glavna pricing konfiguracija
export interface PricingConfig {
  // Režim izračuna cijena
  mode: 'direct' | 'from_cost' | 'category_rules';

  // Konfiguracija nabavne cijene (source polje)
  costPrice?: CostPriceConfig;

  // Kako izračunati REDOVNU cijenu (od nabavne) - GLOBALNO FALLBACK
  regularPriceCalculation?: PriceCalculation;

  // Kako izračunati AKCIJSKU cijenu - GLOBALNO FALLBACK
  salePriceCalculation?: SalePriceCalculation;

  // Pravila cijena po cjenovnim rasponima (npr. 0-100, 100-500, 500+)
  // Ovo ima NAJVIŠI prioritet od globalnih pravila
  priceRangeRules?: PriceRangeRule[];

  // Pravila cijena po kategorijama (override glavnih pravila)
  categoryPriceRules?: CategoryPriceRule[];

  // Pravila cijena po dobavljačima (override glavnih pravila)
  supplierPriceRules?: SupplierPriceRule[];

  // Dobavljači čiji feed nosi MPC umjesto nabavne — njihov supplier_prices iznos ide
  // kao regularna cijena, bez markupa. Prioritet = redoslijed u listi.
  // onlyWhenSole: MPC važi samo dok na proizvodu nema dobavljača sa stvarnom nabavnom.
  // String element = prioritetan dobavljač (kompatibilnost sa prvom verzijom ključa).
  mpcOverride?: {
    enabled: boolean;
    suppliers: (string | { name: string; onlyWhenSole?: boolean })[];
  };

  // @deprecated Zamijenjen sa mpcOverride. Stari config se čita kao suppliers: ['DOPER'].
  doperMpcOverride?: { enabled: boolean };

  // LEGACY - za kompatibilnost
  markupPercent?: number;
  useSourceAsCost?: boolean;
}

// Konfiguracija nabavne cijene - GDJE se nalazi i KAKO formatirati
export interface CostPriceConfig {
  // Da li je uključeno korištenje nabavne cijene
  enabled: boolean;

  // Koje meta polje u source shopu sadrži nabavnu cijenu
  sourceField: string;

  // Fallback ako nabavna cijena ne postoji
  fallback: 'skip' | 'use_regular_price' | 'use_zero';
}

/**
 * Parsira string cijene u broj, automatski prepoznajući format
 * Podržava: "255.00", "255,00", "1.255,00", "1,255.00", "255", "255,000", itd.
 */
export function parsePriceString(value: string | number | undefined | null): number | null {
  if (value === undefined || value === null) return null;

  // Ako je već broj, vrati ga
  if (typeof value === 'number') {
    return isNaN(value) ? null : value;
  }

  // Pretvori u string i očisti
  let str = String(value).trim();

  // Ukloni valutne simbole i razmake
  str = str.replace(/[€$£¥₹KMkm\s]/gi, '');

  // Ako je prazan, vrati null
  if (!str || str === '') return null;

  // Prebroj zareze i tačke
  const commas = (str.match(/,/g) || []).length;
  const dots = (str.match(/\./g) || []).length;

  // Pronađi pozicije zadnjeg zareza i zadnje tačke
  const lastComma = str.lastIndexOf(',');
  const lastDot = str.lastIndexOf('.');

  let cleanedStr = str;

  if (commas === 0 && dots === 0) {
    // Nema separatora, samo broj: "255"
    cleanedStr = str;
  } else if (commas === 0 && dots === 1) {
    // Samo jedna tačka: "255.00" - tačka je decimalni separator
    cleanedStr = str;
  } else if (commas === 1 && dots === 0) {
    // Samo jedan zarez: "255,00" - zarez je decimalni separator
    cleanedStr = str.replace(',', '.');
  } else if (commas === 1 && dots === 1) {
    // Jedan zarez i jedna tačka
    if (lastComma > lastDot) {
      // "1.255,00" - tačka je separator hiljada, zarez decimalni
      cleanedStr = str.replace(/\./g, '').replace(',', '.');
    } else {
      // "1,255.00" - zarez je separator hiljada, tačka decimalni
      cleanedStr = str.replace(/,/g, '');
    }
  } else if (commas > 1 && dots === 0) {
    // Više zareza, bez tačke: "1,255,000" - zarezi su separatori hiljada
    cleanedStr = str.replace(/,/g, '');
  } else if (dots > 1 && commas === 0) {
    // Više tačaka, bez zareza: "1.255.000" - tačke su separatori hiljada
    cleanedStr = str.replace(/\./g, '');
  } else if (commas > 1 && dots === 1) {
    // Više zareza i jedna tačka: "1,255,000.00"
    cleanedStr = str.replace(/,/g, '');
  } else if (dots > 1 && commas === 1) {
    // Više tačaka i jedan zarez: "1.255.000,00"
    cleanedStr = str.replace(/\./g, '').replace(',', '.');
  } else {
    // Nepoznat format, pokušaj očistiti
    // Ako zadnji separator ima 3+ znakova iza sebe, to je separator hiljada
    const lastSep = Math.max(lastComma, lastDot);
    const charsAfterLastSep = str.length - lastSep - 1;

    if (charsAfterLastSep >= 3) {
      // Zadnji separator je separator hiljada
      cleanedStr = str.replace(/[,.]/g, '');
    } else {
      // Zadnji separator je decimalni
      const decimalSep = lastComma > lastDot ? ',' : '.';
      if (decimalSep === ',') {
        cleanedStr = str.replace(/\./g, '').replace(',', '.');
      } else {
        cleanedStr = str.replace(/,/g, '');
      }
    }
  }

  const result = parseFloat(cleanedStr);
  return isNaN(result) ? null : result;
}

// Izračun cijene od nabavne
export interface PriceCalculation {
  // Tip izračuna
  type: 'markup_percent' | 'margin_percent' | 'fixed_amount' | 'multiplier';
  // Vrijednost
  // - markup_percent: 20 = nabavna + 20% = nabavna * 1.20
  // - margin_percent: 20 = nabavna / (1 - 0.20) = nabavna / 0.80
  // - fixed_amount: 10 = nabavna + 10 KM
  // - multiplier: 1.25 = nabavna * 1.25
  value: number;

  // Zaokruživanje
  rounding?: PriceRounding;

  // Minimalna cijena (ne može biti manja od ove)
  minPrice?: number;
}

// Izračun akcijske cijene
export interface SalePriceCalculation {
  // Da li je uključeno
  enabled: boolean;

  // Baza za izračun
  basePrice: 'cost' | 'regular';

  // Tip izračuna (isti kao za redovnu)
  type: 'markup_percent' | 'margin_percent' | 'fixed_amount' | 'multiplier' | 'discount_percent';
  // Vrijednost
  // - discount_percent: -10 = 10% popusta od baze
  value: number;

  // Zaokruživanje
  rounding?: PriceRounding;

  // Minimalna marža (akcijska ne smije biti manja od nabavne + ovo)
  minMarginPercent?: number;
}

// Zaokruživanje cijene
export interface PriceRounding {
  enabled: boolean;
  // Tip zaokruživanja
  // - marketing: Automatsko marketinško zaokruživanje (preko 100 KM na 110,115,119,120... ispod 100 KM na cijele brojeve)
  type: 'ending_99' | 'ending_90' | 'ending_00' | 'ending_50' | 'nearest_5' | 'nearest_10' | 'ceil' | 'floor' | 'round' | 'marketing';
  // Za nearest - na koliko decimala (default 2)
  decimals?: number;
}

/**
 * Marketinško zaokruživanje cijena
 * - Preko 100 KM: zaokruži na marketinške cijene (110, 115, 119, 120, 125, 129, 130, 135, 139, itd.)
 * - Ispod 100 KM: zaokruži na cijele brojeve (55, 56, 57, 58, 59, itd.)
 */
export function applyMarketingRounding(price: number): number {
  if (price >= 100) {
    // Preko 100 KM - zaokruži na marketinške cijene
    // Dozvoljene završetke: 0, 5, 9 za desetice (110, 115, 119, 120, 125, 129, ...)
    const base = Math.floor(price / 10) * 10; // Zaokruži na desetice nadolje
    const remainder = price - base;

    // Odredi najbližu marketinšku cijenu
    // Mogući završeci unutar desetice: 0, 5, 9
    let marketingPrice: number;

    if (remainder <= 2.5) {
      // Bliže 0 (npr. 121, 122 -> 120)
      marketingPrice = base;
    } else if (remainder <= 7) {
      // Bliže 5 (npr. 123, 124, 125, 126, 127 -> 125)
      marketingPrice = base + 5;
    } else {
      // Bliže 9 (npr. 128, 129 -> 129)
      marketingPrice = base + 9;
    }

    // Ako je originalna cijena veća od marketingPrice, idi na sljedeću
    if (price > marketingPrice + 0.5) {
      if (marketingPrice % 10 === 0) {
        marketingPrice += 5;
      } else if (marketingPrice % 10 === 5) {
        marketingPrice += 4; // 5 -> 9
      } else {
        marketingPrice += 1; // 9 -> 10 (sljedeća desetica)
      }
    }

    return marketingPrice;
  } else {
    // Ispod 100 KM - zaokruži na cijele brojeve
    return Math.round(price);
  }
}

/**
 * Primijeni zaokruživanje na cijenu
 * Ako rounding nije definiran, koristi marketinško zaokruživanje kao default
 */
export function applyPriceRounding(price: number, rounding?: PriceRounding): number {
  // Ako rounding uopšte nije definiran - koristi marketinško zaokruživanje kao default
  if (!rounding) {
    return applyMarketingRounding(price);
  }

  // Ako je rounding definiran ali nije enabled - zaokruži na 2 decimale
  if (!rounding.enabled) {
    return Math.round(price * 100) / 100;
  }

  switch (rounding.type) {
    case 'marketing':
      return applyMarketingRounding(price);

    case 'ending_99':
      return Math.floor(price) + 0.99;

    case 'ending_90':
      return Math.floor(price) + 0.90;

    case 'ending_00':
      return Math.round(price);

    case 'ending_50':
      return Math.floor(price) + 0.50;

    case 'nearest_5':
      return Math.round(price / 5) * 5;

    case 'nearest_10':
      return Math.round(price / 10) * 10;

    case 'ceil':
      return Math.ceil(price);

    case 'floor':
      return Math.floor(price);

    case 'round':
    default:
      const decimals = rounding.decimals ?? 2;
      const factor = Math.pow(10, decimals);
      return Math.round(price * factor) / factor;
  }
}

// Pravilo cijene po kategoriji iz source shopa
export interface CategoryPriceRule {
  // ID kategorije iz source shopa
  categoryId: number;
  // Naziv kategorije (za prikaz)
  categoryName: string;

  // Izračun REDOVNE cijene za ovu kategoriju (od nabavne)
  regularPriceCalculation: PriceCalculation;

  // Izračun AKCIJSKE cijene za ovu kategoriju (opcionalno)
  salePriceCalculation?: SalePriceCalculation;

  // Da li je pravilo aktivno
  enabled: boolean;
  // Prioritet (veći broj = veći prioritet, korisno kad proizvod ima više kategorija)
  priority: number;
}

// Price range pravilo za dobavljača
export interface SupplierPriceRange {
  minPrice: number;
  maxPrice: number;
  regularMarkup: number;
  saleMarkup?: number;
  roundingType?: 'none' | 'math' | 'up' | 'down' | 'marketing';
  enabled: boolean;
}

// Pravilo cijene po dobavljaču iz source shopa
export interface SupplierPriceRule {
  // Naziv dobavljača (vrijednost iz meta polja) - NOVO: koristi 'supplier' umjesto 'supplierName'
  supplier: string;
  supplierName?: string; // deprecated, za backward compatibility

  // Default markup za ovog dobavljača (koristi se ako nema odgovarajućeg price range pravila)
  regularMarkup: number;
  saleMarkup?: number;
  roundingType?: 'none' | 'math' | 'up' | 'down' | 'marketing';

  // Price range pravila specifična za ovog dobavljača (opciono)
  // Ako nabavna cijena pada u neki range, koristi se taj markup umjesto default-a
  priceRanges?: SupplierPriceRange[];

  // Legacy polja za backward compatibility
  regularPriceCalculation?: PriceCalculation;
  salePriceCalculation?: SalePriceCalculation;
  pricingMode?: 'custom' | 'range' | 'global' | 'direct_column';

  // Override kolone za nabavnu cijenu (umjesto default nabavna_cijena)
  costPriceColumn?: string;         // e.g. 'regular_price' - koristi ovu kolonu kao bazu za markup

  // Direct column mode - čita finalnu cijenu direktno iz Supabase kolone (bez markupa)
  directPriceColumn?: string;       // e.g. 'regular_price', 'final_price'
  directSalePriceColumn?: string;   // Opciono: kolona za akcijsku cijenu
  directPriceRounding?: 'none' | 'math' | 'up' | 'down' | 'marketing';

  // Da li je pravilo aktivno
  enabled: boolean;
  // Prioritet (veći broj = veći prioritet)
  priority?: number;
}

// Custom meta filter - za filtriranje po bilo kojem meta polju
export interface CustomMetaFilter {
  // Naziv meta polja (npr. '_dobavljac', 'pa_brend', itd.)
  key: string;
  // Operator za poređenje
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'starts_with' | 'ends_with' | 'exists' | 'not_exists' | 'greater_than' | 'less_than';
  // Vrijednost za poređenje (može biti prazan za exists/not_exists)
  value: string;
  // Da li je filter aktivan
  enabled: boolean;
}

// ============================================
// IMAGE SOURCE KONFIGURACIJA
// ============================================

// Konfiguracija izvora slika
// Slike se UVIJEK čitaju iz Supabase products.images kolone (JSONB)
export interface ImageSourceConfig {
  // Da li sinhronizirati slike
  enabled: boolean;

  // Da li prva slika treba biti glavna (featured) slika
  firstImageAsFeatured?: boolean;

  // Da li preskočiti ako target već ima slike
  // TRUE = ne ažuriraj slike ako target shop već ima postavljene slike
  // FALSE = uvijek ažuriraj slike iz Supabase
  skipIfTargetHasImages: boolean;
}

// LEGACY - za backward compatibility
export type ImageSourceType = 'supabase' | 'woo_images' | 'custom_meta_field' | 'woo_with_fallback';

// Default duplicate resolution
export const DEFAULT_DUPLICATE_RESOLUTION: DuplicateResolutionConfig = {
  enabled: false,
  supplierField: '_dobavljac',
  supplierPriorities: [],
  fallbackStrategy: 'first',
  duplicateAction: 'draft',
};

// Default image source config
// Slike se čitaju iz Supabase products.images kolone
export const DEFAULT_IMAGE_SOURCE_CONFIG: ImageSourceConfig = {
  enabled: true,
  firstImageAsFeatured: true,
  skipIfTargetHasImages: true, // Ne ažuriraj ako target već ima slike
};

// ============================================
// AI DESCRIPTION KONFIGURACIJA
// ============================================

// Na koje proizvode primjenjivati AI generiranje opisa
export type AIDescriptionApplyTo = 'new_only' | 'existing_only' | 'all';

// Koje polje generirati
export type AIDescriptionField = 'description' | 'short_description' | 'both';

// AI model za generiranje (Vercel AI Gateway)
export type AIModel =
  | 'google/gemini-2.5-flash'
  | 'google/gemini-2.5-flash-lite'
  | 'openai/gpt-5-mini'
  | 'anthropic/claude-sonnet-4'
  | 'anthropic/claude-sonnet-4.5';

// Kako spojiti AI opis sa postojećim
export type AIDescriptionMergeMode = 'replace' | 'append' | 'prepend';

// Konfiguracija AI generiranja opisa
export interface AIDescriptionConfig {
  // Da li je AI generiranje uključeno
  enabled: boolean;

  // AI model za korištenje
  model: AIModel;

  // Custom prompt za generiranje opisa
  // Može koristiti placeholder varijable: {name}, {sku}, {price}, {categories}, {attributes}, {current_description}
  prompt: string;

  // Na koje proizvode primjenjivati
  applyTo: AIDescriptionApplyTo;

  // Koje polje generirati
  targetField: AIDescriptionField;

  // Da li prepisati postojeći opis ili samo dopuniti
  overwriteExisting: boolean;

  // Da li uključiti web pretragu za dodatne informacije o proizvodu
  enableWebSearch: boolean;

  // Maksimalna dužina generiranog opisa (u karakterima)
  maxLength?: number;

  // Jezik za generiranje opisa
  language: 'bs' | 'hr' | 'sr' | 'en';

  // SEO opcije
  seoOptimization: boolean;

  // Ton pisanja
  tone: 'professional' | 'friendly' | 'luxury' | 'casual' | 'technical';

  // Kako spojiti sa postojećim opisom
  // replace = zamijeni potpuno, append = dodaj AI ispod postojećeg, prepend = dodaj AI iznad postojećeg
  mergeMode?: AIDescriptionMergeMode;

  // Separator između postojećeg i AI opisa (npr. <hr>, ---, prazan red)
  mergeSeparator?: string;
}

// Default AI description config
export const DEFAULT_AI_DESCRIPTION_CONFIG: AIDescriptionConfig = {
  enabled: false,
  model: 'google/gemini-2.5-flash',
  prompt: `Kreiraj SEO optimiziran opis proizvoda na bosanskom jeziku.

Informacije o proizvodu:
- Naziv: {name}
- SKU: {sku}
- Cijena: {price} KM
- Kategorije: {categories}
- Atributi: {attributes}
- Trenutni opis: {current_description}

Upute:
1. Napiši privlačan i informativan opis
2. Uključi ključne riječi za SEO
3. Istakni prednosti i karakteristike proizvoda
4. Koristi prirodan ton pisanja
5. Dužina: 150-300 riječi`,
  applyTo: 'new_only',
  targetField: 'description',
  overwriteExisting: false,
  enableWebSearch: false,
  maxLength: 2000,
  language: 'bs',
  seoOptimization: true,
  tone: 'professional',
};

// Default cost price config
export const DEFAULT_COST_PRICE_CONFIG: CostPriceConfig = {
  enabled: false,
  sourceField: 'nabavna_cijena', // Supabase kolona
  fallback: 'skip',
};

// Default pricing config
export const DEFAULT_PRICING_CONFIG: PricingConfig = {
  mode: 'direct',
  costPrice: DEFAULT_COST_PRICE_CONFIG,
};

// Default conflict resolution
export const DEFAULT_CONFLICT_RESOLUTION: FieldConflictResolution = {
  default: 'overwrite',
  byCategory: {
    images: 'skip_if_exists',      // Slike - preskoči ako već postoje
    categories: 'merge_append',     // Kategorije - dodaj nove
    attributes: 'overwrite',        // Atributi - uvijek prepiši
    descriptions: 'skip_if_exists', // Opisi - preskoči ako postoje
  },
  byField: {},
};

export interface ShopFormData {
  name: string;
  url: string;
  consumerKey: string;
  consumerSecret: string;
}

// Default sync profil
// Default pricing behavior
export const DEFAULT_PRICING_BEHAVIOR = {
  newProducts: 'calculate' as const,
  existingProducts: 'skip' as const,
};

// Default attribute mapping config
export const DEFAULT_ATTRIBUTE_MAPPING_CONFIG: AttributeMappingConfig = {
  enabled: false,
  mappings: [],
  unmappedBehavior: 'keep_original',
};

export const DEFAULT_SYNC_PROFILE: ShopSyncProfile = {
  mode: 'smart',
  fieldsToSync: ['stock', 'regular_price', 'sale_price'],
  options: {
    createNewProducts: false,
    newProductStatus: 'draft',
    updateExistingProducts: true,
    deleteOrphanedProducts: false,
    consolidateDuplicates: false,
    skipProductsWithoutSku: true,
    respectUpdatePriceFlag: true,
    pricingBehavior: DEFAULT_PRICING_BEHAVIOR,
  },
  pricing: DEFAULT_PRICING_CONFIG,
  filters: {},
  customMetaKeys: ['_dobavljac', 'pa_brend', '_nabavna'],
  conflictResolution: DEFAULT_CONFLICT_RESOLUTION,
  duplicateResolution: DEFAULT_DUPLICATE_RESOLUTION,
  imageSource: DEFAULT_IMAGE_SOURCE_CONFIG,
  attributeMappings: DEFAULT_ATTRIBUTE_MAPPING_CONFIG,
  supplierMenuOrder: DEFAULT_SUPPLIER_MENU_ORDER_CONFIG,
};

// Preset sync profili
export const SYNC_PROFILE_PRESETS: Record<string, { name: string; description: string; profile: Partial<ShopSyncProfile> }> = {
  stockOnly: {
    name: 'Samo količine',
    description: 'Sinhronizuj samo stanje zaliha',
    profile: {
      mode: 'update',
      fieldsToSync: ['stock'],
      options: {
        createNewProducts: false,
        updateExistingProducts: true,
        deleteOrphanedProducts: false,
        consolidateDuplicates: false,
        skipProductsWithoutSku: true,
        respectUpdatePriceFlag: false,
        pricingBehavior: { ...DEFAULT_PRICING_BEHAVIOR, existingProducts: 'skip' },
      },
    },
  },
  priceAndStock: {
    name: 'Cijene i količine',
    description: 'Sinhronizuj cijene i stanje zaliha',
    profile: {
      mode: 'update',
      fieldsToSync: ['stock', 'regular_price', 'sale_price'],
      options: {
        createNewProducts: false,
        updateExistingProducts: true,
        deleteOrphanedProducts: false,
        consolidateDuplicates: false,
        skipProductsWithoutSku: true,
        respectUpdatePriceFlag: true,
        pricingBehavior: { ...DEFAULT_PRICING_BEHAVIOR, existingProducts: 'calculate' },
      },
    },
  },
  fullSync: {
    name: 'Kompletna sinhronizacija',
    description: 'Sinhronizuj sve podatke, kreiraj nove artikle',
    profile: {
      mode: 'smart',
      fieldsToSync: ['basic', 'regular_price', 'sale_price', 'stock', 'descriptions', 'images', 'categories', 'attributes', 'meta'],
      options: {
        createNewProducts: true,
        updateExistingProducts: true,
        deleteOrphanedProducts: false,
        consolidateDuplicates: true,
        skipProductsWithoutSku: true,
        respectUpdatePriceFlag: true,
        pricingBehavior: { ...DEFAULT_PRICING_BEHAVIOR, newProducts: 'calculate', existingProducts: 'calculate' },
      },
    },
  },
  createOnly: {
    name: 'Samo kreiranje',
    description: 'Kreiraj samo nove artikle, ne ažuriraj postojeće',
    profile: {
      mode: 'create',
      fieldsToSync: ['basic', 'regular_price', 'sale_price', 'stock', 'descriptions', 'images', 'categories', 'attributes', 'meta'],
      options: {
        createNewProducts: true,
        updateExistingProducts: false,
        deleteOrphanedProducts: false,
        consolidateDuplicates: false,
        skipProductsWithoutSku: true,
        respectUpdatePriceFlag: false,
        pricingBehavior: { ...DEFAULT_PRICING_BEHAVIOR, newProducts: 'calculate', existingProducts: 'skip' },
      },
    },
  },
};

// Legacy kompatibilnost
export interface ShopSyncOptions {
  updateQuantities: boolean;
  updatePrices: boolean;
  updateImages: boolean;
  consolidateDuplicates: boolean;
  priceMarkup?: number;
}

export const DEFAULT_SYNC_OPTIONS: ShopSyncOptions = {
  updateQuantities: true,
  updatePrices: false,
  updateImages: false,
  consolidateDuplicates: false,
};

// ============================================
// TAXONOMY MAPPING (Kategorije i Tagovi)
// ============================================

// WooCommerce kategorija/tag
export interface WooTaxonomyItem {
  id: number;
  name: string;
  slug: string;
  parent: number;
  description?: string;
  count?: number;
}

// Tip mapiranja
export type TaxonomyMappingType =
  | 'existing'      // Mapira na postojeću kategoriju/tag u target shopu
  | 'create_new'    // Kreira novu kategoriju/tag u target shopu
  | 'custom_field'; // Mapira na custom field

// Pojedinačno mapiranje kategorije/taga
export interface TaxonomyMapping {
  // Source kategorija/tag ID
  sourceId: number;
  // Source naziv (za prikaz)
  sourceName: string;
  // Source slug
  sourceSlug: string;
  // Parent ID u source shopu (za kategorije)
  sourceParent?: number;

  // Tip mapiranja
  mappingType: TaxonomyMappingType;

  // Ako je mappingType = 'existing': ID target kategorije/taga
  targetId?: number;
  // Target naziv (za prikaz)
  targetName?: string;

  // Ako je mappingType = 'create_new': naziv nove kategorije/taga
  newName?: string;
  // Parent ID za novu kategoriju (opcionalno)
  newParent?: number;

  // Ako je mappingType = 'custom_field': naziv custom fielda
  customFieldKey?: string;
  // Vrijednost za custom field (ako je različita od source naziva)
  customFieldValue?: string;

  // Da li je mapiranje aktivno
  enabled: boolean;
}

// Konfiguracija mapiranja za kategorije ili tagove
export interface TaxonomyMappingConfig {
  // Da li je mapiranje uključeno
  enabled: boolean;

  // Izvor mapiranja: source_categories ili custom_field
  mappingSource?: 'source_categories' | 'custom_field';

  // Mapiranja (za source_categories mode)
  mappings: TaxonomyMapping[];

  // Šta raditi sa kategorijama/tagovima koji nisu mapirani
  unmappedBehavior: 'skip' | 'create_as_is' | 'use_default';

  // Default target kategorija/tag za unmapped (ako je unmappedBehavior = 'use_default')
  defaultTargetId?: number;
  defaultTargetName?: string;

  // Kada primjenjivati mapiranje
  // Primijeni mapiranje na nove proizvode (create)
  applyToNewProducts: boolean;
  // Primijeni mapiranje na postojeće proizvode (update)
  applyToExistingProducts: boolean;

  // === CUSTOM FIELD MODE ===
  // Naziv custom fielda iz kojeg se čitaju kategorije (npr. '_kategorije', '_grupaimport')
  // Koristi se samo kada je mappingSource = 'custom_field'
  customFieldSourceKey?: string;

  // Mapiranje iz custom fielda (source custom field vrijednost → target kategorija/tag)
  customFieldMappings?: CustomFieldToTaxonomyMapping[];
}

// Mapiranje source custom field vrijednosti na target kategoriju/tag
export interface CustomFieldToTaxonomyMapping {
  // Naziv custom fielda u source shopu (npr. '_kategorija', '_tip_proizvoda')
  sourceFieldKey: string;
  // Vrijednost custom fielda koju mapiramo
  sourceFieldValue: string;

  // Target kategorija/tag ID
  targetId: number;
  // Target naziv (za prikaz)
  targetName: string;

  // Da li je mapiranje aktivno
  enabled: boolean;
}

// Kompletna konfiguracija taxonomy mapiranja za shop
export interface ShopTaxonomyMappings {
  // Mapiranje kategorija
  categories: TaxonomyMappingConfig;
  // Mapiranje tagova
  tags: TaxonomyMappingConfig;
}

// Default taxonomy mapping config
export const DEFAULT_TAXONOMY_MAPPING_CONFIG: TaxonomyMappingConfig = {
  enabled: false,
  mappings: [],
  unmappedBehavior: 'create_as_is',
  applyToNewProducts: true,
  applyToExistingProducts: false,
};

// Default shop taxonomy mappings
export const DEFAULT_SHOP_TAXONOMY_MAPPINGS: ShopTaxonomyMappings = {
  categories: { ...DEFAULT_TAXONOMY_MAPPING_CONFIG },
  tags: { ...DEFAULT_TAXONOMY_MAPPING_CONFIG },
};

// ============================================
// WEBHOOK KONFIGURACIJA
// ============================================

// Webhook konfiguracija za real-time sync
export interface WebhookConfig {
  // Da li je webhook aktivan
  enabled: boolean;

  // Secret za verifikaciju webhook payloada (generira se automatski)
  secret: string;

  // Webhook URL koji source shop treba pozvati
  // Format: https://sync.sejkan.com/api/webhook/{shopId}
  webhookUrl?: string;

  // Koji eventi triggeraju sync
  events: WebhookEvent[];

  // Debounce - čekaj X sekundi prije nego procesuiraš (za batch-ovanje brzih promjena)
  debounceSeconds: number;

  // Retry konfiguracija
  maxRetries: number;
  retryDelaySeconds: number;
}

// Webhook eventi koje podržavamo
export type WebhookEvent =
  | 'product.created'
  | 'product.updated'
  | 'product.deleted'
  | 'product.restored';

// Default webhook config
export const DEFAULT_WEBHOOK_CONFIG: WebhookConfig = {
  enabled: false,
  secret: '',
  events: ['product.created', 'product.updated'],
  debounceSeconds: 5,
  maxRetries: 3,
  retryDelaySeconds: 30,
};

// Generiraj webhook secret
export function generateWebhookSecret(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = 'whsec_';
  for (let i = 0; i < 32; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}
